import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { AccountingAccountFindInterface } from '@zaoblako/shared/modules/catalog/dtos/accounting-account/accounting-account-find.dto';
import { AccountingAccountQueryInterface } from '@zaoblako/shared/modules/catalog/dtos/accounting-account/accounting-account-query.dto';
import { BasisAccountingAccountService } from '@zaoblako/web/core/services/basis/basis-accounting-account.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';

@Component({
  selector: 'crm-accounting-account-single-select',
  standalone: true,
  imports: [CommonModule, SingleSelectComponent, SpinnerComponent],
  templateUrl: './accounting-account-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountingAccountSingleSelectComponent implements OnInit {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public accountingAccounts: AccountingAccountFindInterface[] = [];
  public queryParams: AccountingAccountQueryInterface = {
    limit: 40,
    offset: 0
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedAccountingAccount: AccountingAccountFindInterface;
  @Input() public set selectedAccountingAccount(selectedAccountingAccount: AccountingAccountFindInterface) {
    if (this.customSearch) {
      this._selectedAccountingAccount = selectedAccountingAccount;
      return;
    }

    if (this.accountingAccounts.some((accountingAccount) => accountingAccount.id === selectedAccountingAccount?.id)) {
      this._selectedAccountingAccount = this.accountingAccounts.find(
        (accountingAccount) => accountingAccount.id === selectedAccountingAccount.id
      );
      return;
    }

    if (selectedAccountingAccount?.id) {
      if (typeof selectedAccountingAccount.id === 'string') {
        selectedAccountingAccount.id = +selectedAccountingAccount.id;
      }

      this.basisAccountingAccountService
        .findWithPagination({ includeIds: [selectedAccountingAccount.id], limit: 1, offset: 0 })
        .subscribe({
          next: (data) => {
            if (data.items[0]) {
              const _accountingAccount = data.items[0];
              if (
                !this.accountingAccounts.some((accountingAccount) => accountingAccount.id === _accountingAccount.id)
              ) {
                this.accountingAccounts = this.accountingAccounts.concat(_accountingAccount);
                this.cdRef.markForCheck();
              }
              setTimeout(() => {
                this._selectedAccountingAccount = _accountingAccount;
                this.cdRef.markForCheck();
              }, 30);
            }
          }
        });
    }
  }

  @Output() public selectedAccountingAccountChange = new EventEmitter<AccountingAccountFindInterface>();

  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public showClear = true;
  @Input() public labelInput = false;

  @Input() public customSearch = false;

  @Input() public set setSelectedAccountingAccountId(id: number) {
    if (id === null || id === undefined) {
      return;
    }

    let _id = id;
    if (typeof id === 'string') {
      _id = +id;
    }

    if (this.accountingAccounts.some((accountingAccount) => accountingAccount.id === _id)) {
      this._selectedAccountingAccount = this.accountingAccounts.find(
        (accountingAccount) => accountingAccount.id === _id
      );
      return;
    }

    this.basisAccountingAccountService.findWithPagination({ includeIds: [_id], limit: 1, offset: 0 }).subscribe({
      next: (data) => {
        if (data.items[0]) {
          const _accountingAccount = data.items[0];
          if (!this.accountingAccounts.some((accountingAccount) => accountingAccount.id === _accountingAccount.id)) {
            this.accountingAccounts = this.accountingAccounts.concat(_accountingAccount);
            this.cdRef.markForCheck();
          }
          setTimeout(() => {
            this._selectedAccountingAccount = _accountingAccount;
            this.cdRef.markForCheck();
          }, 30);
        }
      }
    });
  }

  public constructor(
    private readonly basisAccountingAccountService: BasisAccountingAccountService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.basisAccountingAccountService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          this.accountingAccounts = this.accountingAccounts?.length
            ? this.accountingAccounts.concat(
                data.items.filter(
                  (item) => !this.accountingAccounts.some((accountingAccount) => accountingAccount.id === item.id)
                )
              )
            : data.items.filter(
                (item) => !this.accountingAccounts.some((accountingAccount) => accountingAccount.id === item.id)
              );
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public onSelect(accountingAccount: AccountingAccountFindInterface) {
    this.selectedAccountingAccountChange.emit(accountingAccount);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.accountingAccounts.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.basisAccountingAccountService.findWithPagination(this.queryParams).subscribe({
                next: (data) => {
                  this.accountingAccounts = this.accountingAccounts.concat(
                    data.items.filter(
                      (item) => !this.accountingAccounts.some((accountingAccount) => accountingAccount.id === item.id)
                    )
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 100);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.basisAccountingAccountService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          if (
            !data.items.some((item) => item.id === this._selectedAccountingAccount?.id) &&
            this._selectedAccountingAccount
          ) {
            this.accountingAccounts = data.items.concat(this._selectedAccountingAccount);
          } else {
            this.accountingAccounts = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public toItem(item: any): AccountingAccountFindInterface {
    return item as AccountingAccountFindInterface;
  }
}
